import { useMemo } from 'react';
import {
  GroupedTransactionsDTO,
  TransactionDTO,
} from '../../../../../../../api/useGroupedTransactionsQuery';
import { SortBy } from '../ActivityDetails.meta';

const sortGroups = (groups: GroupedTransactionsDTO['groupedTransactions'], sortBy: SortBy) => {
  return [...groups].sort((a, b) => {
    if (sortBy.column === 'groupName') {
      return sortBy.sort === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
    }

    if (sortBy.column === 'total') {
      return sortBy.sort === 'asc' ? a.total - b.total : b.total - a.total;
    }

    return 0;
  });
};

const sortTransactions = (transactions: TransactionDTO[], sortBy: SortBy) => {
  return [...transactions].sort((a, b) => {
    if (sortBy.column === 'date') {
      return sortBy.sort === 'asc'
        ? new Date(a.date).getTime() - new Date(b.date).getTime()
        : new Date(b.date).getTime() - new Date(a.date).getTime();
    }

    const aValue = a[sortBy.column];
    const bValue = b[sortBy.column];

    if (
      sortBy.column === 'name' ||
      sortBy.column === 'ticker' ||
      sortBy.column === 'description' ||
      sortBy.column === 'confirmNotes' ||
      sortBy.column === 'accountNumber'
    ) {
      return sortBy.sort === 'asc'
        ? (aValue ?? '').localeCompare(bValue ?? '')
        : (bValue ?? '').localeCompare(aValue ?? '');
    }

    if (sortBy.column === 'units' || sortBy.column === 'price' || sortBy.column === 'total') {
      return sortBy.sort === 'asc' ? (aValue ?? 0) - (bValue ?? 0) : (bValue ?? 0) - (aValue ?? 0);
    }

    return 0;
  });
};

interface Params {
  data: GroupedTransactionsDTO | null;
  sortBy: SortBy;
}

export const useSortedData = ({ data, sortBy }: Params) => {
  return useMemo(() => {
    if (!data) {
      return null;
    }

    if (sortBy.sort === 'none') {
      return data;
    }

    return {
      ...data,
      groupedTransactions: sortGroups(data.groupedTransactions, sortBy).map(group => ({
        ...group,
        transactions: sortTransactions(group.transactions, sortBy),
      })),
    };
  }, [data, sortBy]);
};
